/**
 * Server-sync side of the Cost Scheme agenda: persisting the board, "Save & approve", term inheritance
 * overrides, and the React-Query invalidations each of those needs. Keeps all mutation wiring out of the
 * presentational orchestrator ({@link CostSchemeContent}).
 *
 * Invalidation policy (deliberate):
 *  - A parts save touches only the LAYER-state consumers (detail pills + navigation tree); it does NOT
 *    invalidate the schema query, because the board model is authoritative (client-generated ids) and a
 *    re-seed would close an open drawer / interrupt editing.
 *  - An inheritance override changes the effective schema owner, so it additionally invalidates the schema
 *    query to re-seed the board from fresh data.
 */
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getGetVenuaPlannerSerieCostSchemeTermsQueryKey,
  getGetVenuaPlannerSerieDetailQueryKey,
  getGetVenuaPlannerSerieProgramSchemeQueryKey,
  getGetVenuaPlannerTermDetailQueryKey,
  getGetVenuaPlannerTermProgramSchemeQueryKey,
  useCreateVenuaPlannerTermSchemeOverride,
  useRemoveVenuaPlannerTermSchemeOverride,
  useSaveVenuaPlannerSerieProgramScheme,
  useSaveVenuaPlannerTermProgramScheme,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type { LayerState, SerieLayer } from '@/api/generated/schemas';
import { toast } from '@/shared/ui';
import { venuaPlannerQueryKeys } from '../../../venuaPlannerApi';
import { useChangeSerieLayerState, useChangeTermLayerState } from '../useChangeLayerState';
import { toSaveRequest } from './model';
import type { CostSchemeBoard } from './useCostSchemeBoard';

type Scope = 'serie' | 'term';

interface UseCostSchemeSyncOptions {
  scope: Scope;
  productId: string;
  serieId: string;
  termId?: string;
  board: CostSchemeBoard;
  readOnly: boolean;
}

export interface CostSchemeSync {
  /** Persist the board; returns whether it succeeded. Records the saved change token on success. */
  save: () => Promise<boolean>;
  /** Void flush for the autosave action bar (drops the boolean result). */
  autosaveFlush: () => Promise<void>;
  /**
   * Flush unsaved board edits before a layer-state action (skips when read-only or clean); a `false`
   * return aborts the action. Wired to {@link ApproveLayerButton} `onBeforeAction`.
   */
  saveBeforeAction: () => Promise<boolean>;
  /**
   * Change the Product cost scheme layer state (Prepare / Approve / Return / Unapprove) through the
   * unified state-change endpoint (serie or term scope). Toasts + invalidations are handled inside.
   */
  changeState: (layer: SerieLayer, targetState: LayerState) => Promise<boolean>;
  /** A save-before or a state-change mutation is in flight. */
  changing: boolean;
  /** Create or remove the term's schema override; re-seeds the board from the fresh schema. */
  runOverride: (action: 'createOverride' | 'removeOverride') => Promise<void>;
  overridePending: boolean;
}

export function useCostSchemeSync({
  scope,
  productId,
  serieId,
  termId,
  board,
  readOnly,
}: UseCostSchemeSyncOptions): CostSchemeSync {
  const { t } = useTranslation('venua-planner');
  const queryClient = useQueryClient();

  const saveSerie = useSaveVenuaPlannerSerieProgramScheme();
  const saveTerm = useSaveVenuaPlannerTermProgramScheme();
  // Oba hooky se volají bezpodmínečně (pravidla hooků); použije se jen ten dle scope.
  const serieChange = useChangeSerieLayerState({ productId, serieId });
  const termChange = useChangeTermLayerState({ productId, serieId, termId: termId ?? '' });
  const activeChangeState = scope === 'serie' ? serieChange.changeState : termChange.changeState;
  const activePending = scope === 'serie' ? serieChange.isPending : termChange.isPending;
  const createOverride = useCreateVenuaPlannerTermSchemeOverride();
  const removeOverride = useRemoveVenuaPlannerTermSchemeOverride();

  const schemaQueryKey = useMemo(
    () =>
      scope === 'serie'
        ? getGetVenuaPlannerSerieProgramSchemeQueryKey(productId, serieId)
        : getGetVenuaPlannerTermProgramSchemeQueryKey(productId, serieId, termId ?? ''),
    [scope, productId, serieId, termId]
  );

  // Layer-state consumers (detail pills + navigation tree). A parts save touches only these — the board
  // model stays authoritative (client-generated ids), so the schema query is deliberately NOT invalidated,
  // which would re-seed and close an open drawer / interrupt editing.
  const invalidateLayer = useCallback(() => {
    if (scope === 'serie') {
      void queryClient.invalidateQueries({
        queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
      });
    } else {
      void queryClient.invalidateQueries({
        queryKey: getGetVenuaPlannerTermDetailQueryKey(productId, serieId, termId ?? ''),
      });
    }
    void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
    // Postranní panel termínů (list view série) přepočítá sdílené i vlastní součty po každém uložení/override.
    void queryClient.invalidateQueries({
      queryKey: getGetVenuaPlannerSerieCostSchemeTermsQueryKey(productId, serieId),
    });
  }, [queryClient, scope, productId, serieId, termId]);

  // Inheritance override changes the effective schema owner — re-seed the board from the fresh schema.
  const invalidateWithSchema = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: schemaQueryKey });
    invalidateLayer();
  }, [queryClient, schemaQueryKey, invalidateLayer]);

  // Autosave/manual flush: persist the whole board, then record the token that was saved. Newly created
  // parts already carry client-generated UUIDv7 ids, so there is nothing to reconcile — the model stays
  // authoritative and the drawer keeps its selection. Success is signalled by the action bar's status
  // note (no toast spam on every autosave); only failures toast.
  const save = useCallback(async (): Promise<boolean> => {
    if (readOnly) return false;
    const token = board.changeToken;
    const data = toSaveRequest(board.model);
    try {
      if (scope === 'serie') {
        await saveSerie.mutateAsync({ productId, serieId, data });
      } else {
        await saveTerm.mutateAsync({ productId, serieId, termId: termId ?? '', data });
      }
      board.markSaved(token);
      invalidateLayer();
      return true;
    } catch {
      toast({
        severity: 'error',
        title: t('costScheme.toast.errorTitle'),
        description: t('costScheme.toast.saveError'),
      });
      return false;
    }
  }, [readOnly, board, scope, saveSerie, saveTerm, productId, serieId, termId, invalidateLayer, t]);

  const autosaveFlush = useCallback(async () => {
    await save();
  }, [save]);

  // Uložení rozpracovaného boardu před akcí – `ApproveLayerButton` ho volá přes `onBeforeAction`. Když
  // uložení selže, akce se přeruší. Vrácení / vrácení schválení běží nad read-only boardem, kde se neukládá.
  const saveBeforeAction = useCallback(async (): Promise<boolean> => {
    if (readOnly || !board.dirty) return true;
    return save();
  }, [readOnly, board.dirty, save]);

  // Změna stavu vrstvy Product cost scheme (Připravit / Schválit / Vrátit / Vrátit schválení) přes sjednocený
  // endpoint. Toasty i sdílené invalidace řeší hook; navíc přepočítáme postranní panel termínů série.
  const changeState = useCallback(
    async (layer: SerieLayer, targetState: LayerState): Promise<boolean> => {
      const ok = await activeChangeState(layer, targetState);
      invalidateLayer();
      return ok;
    },
    [activeChangeState, invalidateLayer]
  );

  const runOverride = useCallback(
    async (action: 'createOverride' | 'removeOverride') => {
      if (scope !== 'term' || !termId) return;
      try {
        if (action === 'createOverride') {
          await createOverride.mutateAsync({ productId, serieId, termId });
          toast({ severity: 'success', description: t('costScheme.toast.overrideCreated') });
        } else {
          await removeOverride.mutateAsync({ productId, serieId, termId });
          toast({ severity: 'success', description: t('costScheme.toast.overrideRemoved') });
        }
        invalidateWithSchema();
      } catch {
        toast({
          severity: 'error',
          title: t('costScheme.toast.errorTitle'),
          description: t('costScheme.toast.overrideError'),
        });
      }
    },
    [scope, termId, createOverride, removeOverride, productId, serieId, invalidateWithSchema, t]
  );

  return {
    save,
    autosaveFlush,
    saveBeforeAction,
    changeState,
    changing: activePending || saveSerie.isPending || saveTerm.isPending,
    runOverride,
    overridePending: createOverride.isPending || removeOverride.isPending,
  };
}
